import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { type IIconsVector } from 'shared/ui/Icon/icons'
import { truthy } from 'shared/lib'
import { type IInboxKey } from 'entities/Inbox/model/types'
import { TYPES_INBOX, NAME_INBOX } from 'entities/Inbox/store/types'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import {
  type IResponseConversationMentionUnread,
  type IResponseConversationUnread,
} from 'entities/Conversation/api/types'
import { type ICallSettingsItem } from 'entities/Organization/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { BaseInbox } from 'entities/Inbox/model/BaseInbox'
import { type IResponseBusinessHours } from 'entities/Users/api/types.ts'

export class Inbox extends BaseInbox<IResponseInbox> {
  id: number
  disabled!: boolean
  organization_id!: number
  numberIds!: number[]
  formatedNumbers!: string[]
  deprioritized_numbers!: string[] | null
  numberId!: number
  memberIds!: number[]
  ownerId!: number
  is_geomatch!: boolean
  is_hubspot_assignment!: boolean
  is_round_robin!: boolean
  is_smart!: boolean
  is_sinch!: boolean
  is_rcs!: boolean
  is_short_code!: boolean
  type: 'inbox'
  teamsIds!: number[]
  is_rename!: boolean
  description?: string
  root_tree!: string
  is_muted!: boolean
  unread!: number
  unread_calls!: number
  unread_messages_count!: number
  unread_mentions_count!: number
  color_coding_enabled!: boolean
  call_via_aircall!: ICallSettingsItem | null
  sending_options: IResponseInbox['sending_options'] | null = null
  inbox_identifier: IResponseInbox['inbox_identifier'] | null = null
  messaging_channel_type!: IResponseInbox['messaging_channel_type']
  messaging_channel_entity!: IResponseInbox['messaging_channel_entity']
  updatedAt = ''
  origin!: IResponseInbox

  constructor(item: IResponseInbox, private _entities: IEntitiesStore) {
    super(item)

    this.id = item.id
    this.type = 'inbox'
    this.syncOrigin(item)

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'updateInboxBusinessHours' && event.newValue) {
        const data: IResponseBusinessHours = JSON.parse(event.newValue)
        if (data.hourable_id === this.id) {
          this.updateBusinessHours(data)
        }
      }
    })

    makeObservable(this, {
      name: observable,
      origin: observable,
      is_rename: observable,
      is_favorite: observable,
      root_tree: observable,
      unread: observable,
      unread_calls: observable,
      unread_messages_count: observable,
      unread_mentions_count: observable,
      is_muted: observable,
      deprioritized_numbers: observable,
      messaging_channel_type: observable,
      messaging_channel_entity: observable,
      memberIds: observable,
      ownerId: observable,
      updatedAt: observable,
      availableNumberIds: computed,
      callerIdFailedReason: computed,
      isNumberNotVerifiedCallerId: computed,
      unreadCount: computed,
      rcsProfile: computed,
      handleUpdateIsMuted: action,
      handleUpdateUnreadCount: action,
      handleUpdateUnreadMentionsCount: action,
      changeOwnerId: action,
      disabled: observable,
    })
  }

  handleUpdateIsMuted = (status: boolean) => {
    this.is_muted = status
  }

  syncOrigin = (origin: IResponseInbox & { root_tree?: string; is_rename?: boolean }) => {
    this.id = origin.id
    this.organization_id = origin.organization_id
    this.is_favorite = origin.is_favorite
    this.is_geomatch = origin.is_geomatch
    this.is_hubspot_assignment = origin.is_hubspot_assignment
    this.is_round_robin = origin.is_round_robin
    this.is_smart = origin.is_smart
    this.is_rcs = origin.is_rcs
    this.is_short_code = Boolean(origin?.number?.short_code)
    this.name = origin.name
    this.unread = origin.unread || 0
    this.unread_calls = origin.unread_calls || 0
    this.unread_messages_count = origin.unread_messages_count || 0
    this.unread_mentions_count = origin.unread_mentions_count || 0
    this.numberId = origin.number ? origin.number.id : 0
    this.numberIds = Array.isArray(origin.numbers) ? origin.numbers.map((item) => item.id) : []
    this.formatedNumbers = Array.isArray(origin.numbers)
      ? origin.numbers.map((item) => item.formatted_number)
      : []
    this.memberIds = origin.member_ids
    this.ownerId = origin.owner_id
    this.teamsIds = []
    this.is_rename = Boolean(origin.is_rename)
    this.description = origin.description
    this.root_tree = origin.root_tree || this.initRootTree()
    this.is_muted = origin.is_muted
    this.color_coding_enabled = origin.color_coding_enabled
    this.origin = origin
    this.call_via_aircall = origin.call_via_aircall
    this.sending_options = origin.sending_options
    this.inbox_identifier = origin.inbox_identifier
    this.deprioritized_numbers = origin.deprioritized_numbers
    this.is_sinch = Boolean(origin?.number?.number_vendor.key === 'sinch')
    this.messaging_channel_type = origin.messaging_channel_type
    this.messaging_channel_entity = origin.messaging_channel_entity
    this.disabled = Boolean(origin.disabled)
    this.updatedAt = origin.updated_at
  }

  handleUpdateUnreadCount = (data: IResponseConversationUnread) => {
    this.unread = data.unread
    this.unread_messages_count = data.unread_messages_count
    this.unread_calls = data.unread_calls
  }

  handleUpdateUnreadMentionsCount = (data: IResponseConversationMentionUnread) => {
    this.unread_mentions_count = data.unread_mentions_count
  }

  changeOwnerId = (id: number) => {
    this.ownerId = id
  }

  removeMemberId = (memberId: number) => {
    this.memberIds = this.memberIds.filter((id) => id !== memberId)
  }

  get unreadCount() {
    return this.unread_messages_count + this.unread_mentions_count
  }

  get number() {
    return this._entities.numbersStore.getItem(this.numberId)
  }

  get numbers() {
    return this.numberIds
      .map(
        (id, index) =>
          this._entities.numbersStore.getItem(id) ||
          new Phone(this.origin.numbers[index], this._entities)
      )
      .filter(truthy)
  }

  get deprioritizedNumberIds() {
    return this.deprioritized_numbers ? this.deprioritized_numbers.map((item) => Number(item)) : []
  }

  get rcsProfile() {
    if (
      this.messaging_channel_type === 'rcs_sender' &&
      this.messaging_channel_entity &&
      'twilio_rcs_sender_sid' in this.messaging_channel_entity
    ) {
      return this.messaging_channel_entity
    }

    return null
  }

  get availableNumberIds() {
    return this.numberIds.filter((id) => !this.deprioritizedNumberIds.includes(id))
  }

  get isMainIdentifier() {
    if (!this.inbox_identifier) return false

    return /^main-inbox(.*)/.test(this.inbox_identifier)
  }

  get isTrialIdentifier() {
    if (!this.inbox_identifier) return false

    return /^trial-inbox(.*)/.test(this.inbox_identifier)
  }

  get isCreateGroup() {
    return this.id === NAME_INBOX.Create
  }

  get isNumberNotVerifiedCallerId() {
    return this.number?.isNotVerifiedCallerId
  }

  get callerIdFailedReason() {
    return this.isNumberNotVerifiedCallerId && this.number?.callerIdFailedReason
  }

  get icon(): IIconsVector {
    if (this.isRCS) return 'rcs'
    if (this.isShortCode) return 'hash2'
    if (this.isUnifiedInbox) return 'all_inboxes'
    if (this.isGroupInbox) return 'folder'

    return 'inboxes2'
  }

  get inboxType(): TYPES_INBOX {
    if (this.is_smart) return TYPES_INBOX.SMART

    return TYPES_INBOX.SHARED
  }

  get typeKey(): IInboxKey {
    return 'shared'
  }

  get currentInboxType(): TYPES_INBOX {
    if (this.isGroupInbox) return TYPES_INBOX.GROUP
    if (this.isSmartInbox) return TYPES_INBOX.SMART
    if (this.isSharedInbox) return TYPES_INBOX.SHARED

    return TYPES_INBOX.UNIFIED
  }

  get isSinch() {
    return this.is_sinch
  }

  get isShortCode() {
    return this.is_short_code
  }

  get isRCS() {
    return this.is_rcs
  }

  get bodyUpdateUserTeam() {
    return {
      id: this.id,
      type: this.currentInboxType,
    }
  }

  get bodyUpdateTeamsGroupsById() {
    return {
      name: this.name,
      description: this.description,
      team_ids: this.teamsIds,
    }
  }

  get idTree() {
    return `${this.root_tree}_${this.id}`
  }

  get numberOrCount() {
    if (this.numberIds.length > 1) {
      return `${this.numberIds.length} numbers`
    }

    const number = this._entities.numbersStore.getItem(this.numberId)

    return number ? number.formatted_number : ''
  }

  get isUseOrganizationCallSettings() {
    return this._entities.numbersStore.getItem(this.numberId)?.isUseOrganizationCallSettings
  }

  get isNumberOutboundCalls() {
    if (this.isUseOrganizationCallSettings) {
      return this._entities.organizationStore.organization_call?.is_active
    }

    return this._entities.numbersStore.getItem(this.numberId)?.voice_outbound
  }

  get isNumberInboundCalls() {
    if (this.isUseOrganizationCallSettings) {
      return this._entities.organizationStore.organization_call?.is_active_inbound
    }

    return this._entities.numbersStore.getItem(this.numberId)?.voice
  }

  get isMuted() {
    return this.is_muted
  }

  get isPFTInbox() {
    return (
      this._entities.subscriptionStore.isTrial && this.number?.number_vendor?.key === 'inteliquent'
    )
  }

  get isCallViaAircallActive() {
    return this.call_via_aircall?.is_active
  }

  get sendingOptions() {
    if (!this.sending_options) return []

    return this.sending_options.smart
  }

  getTypeNumber = (numberId: number) => {
    const scaleType = this.sendingOptions.find((item) => item.number_ids.includes(numberId))?.key

    if (scaleType) return scaleType

    return numberId
  }

  updateBusinessHours = (data: IResponseBusinessHours) => {
    this.origin = {
      ...this.origin,
      business_hours: data,
    }
  }
}
